import React, { useState, useEffect, useRef } from "react";
import api from "../api/client";
import { GradientText } from "../theme/scala";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Chip,
  Fade,
  IconButton,
  Tooltip,
  Paper,
  Stack,
  CircularProgress,
} from "@mui/material";
import {
  StorageRounded as Storage,
  CloseRounded as CloseIcon,
  DnsRounded,
  DeleteSweepRounded as ClearIcon,
  TerminalRounded as TerminalIcon,
  PlayArrowRounded as RunIcon,
} from "@mui/icons-material";

export default function Databases() {
  const [servers, setServers] = useState([]);
  const [selectedServer, setSelectedServer] = useState("");
  const [serverObj, setServerObj] = useState(null);

  // Terminal State'leri - MANTIĞIN AYNI KALDI
  const [isTerminalOpen, setIsTerminalOpen] = useState(false);
  const [currentDb, setCurrentDb] = useState("");
  const [history, setHistory] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isExecuting, setIsExecuting] = useState(false);

  // Komut Geçmişi - MANTIĞIN AYNI KALDI
  const [commandHistory, setCommandHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);

  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const fetchServers = async () => {
      try {
        const res = await api.get("/servers");
        setServers(res.data);
      } catch (err) {}
    };
    fetchServers();
  }, []);

  useEffect(() => {
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [history]);

  const handleConnect = () => {
    if (!selectedServer) return;
    const srv = servers.find((s) => s.id === selectedServer);
    setServerObj(srv);
    setHistory([
      { type: "info", text: `Visione SQL Tunnel v1.0.0 Online` },
      {
        type: "info",
        text: `Bağlantı Hedefi: ${srv.name} (${srv.ip_address})`,
      },
      {
        type: "info",
        text: `Sorguları bitirmek için ';' kullanın.`,
      },
    ]);
    setCurrentDb("");
    setCommandHistory([]);
    setHistoryIndex(-1);
    setIsTerminalOpen(true);
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  const handleClearTerminal = () => {
    setHistory([]);
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  // ASCII Tablo Çevirici - MANTIĞIN AYNI KALDI
  const formatAsAsciiTable = (data) => {
    if (!data || data.length === 0) return "Empty set (0.00 sec)";
    if (data.affectedRows !== undefined) {
      return `Query OK, ${data.affectedRows} row(s) affected`;
    }
    const keys = Object.keys(data[0]);
    const colWidths = {};
    keys.forEach((k) => {
      colWidths[k] = k.length;
      data.forEach((row) => {
        const val = row[k] !== null ? String(row[k]) : "NULL";
        if (val.length > colWidths[k]) colWidths[k] = val.length;
      });
    });
    const separator =
      "+" + keys.map((k) => "-".repeat(colWidths[k] + 2)).join("+") + "+";
    const header =
      "|" +
      keys.map((k) => " " + k.padEnd(colWidths[k], " ") + " ").join("|") +
      "|";
    const rows = data.map((row) => {
      return (
        "|" +
        keys
          .map((k) => {
            const val = row[k] !== null ? String(row[k]) : "NULL";
            return " " + val.padEnd(colWidths[k], " ") + " ";
          })
          .join("|") +
        "|"
      );
    });
    return [
      separator,
      header,
      separator,
      ...rows,
      separator,
      `${data.length} row(s) in set`,
    ].join("\n");
  };

  // Klavye Olayları - MANTIĞIN AYNI KALDI
  const handleKeyDown = async (e) => {
    if (e.key === "ArrowUp") {
      e.preventDefault();
      if (commandHistory.length > 0) {
        const newIndex =
          historyIndex === -1
            ? commandHistory.length - 1
            : Math.max(0, historyIndex - 1);
        setHistoryIndex(newIndex);
        setInputValue(commandHistory[newIndex]);
      }
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (historyIndex !== -1) {
        const newIndex = historyIndex + 1;
        if (newIndex >= commandHistory.length) {
          setHistoryIndex(-1);
          setInputValue("");
        } else {
          setHistoryIndex(newIndex);
          setInputValue(commandHistory[newIndex]);
        }
      }
      return;
    }
    if (e.key === "Enter" && inputValue.trim()) {
      const query = inputValue.trim();
      setInputValue("");
      if (commandHistory[commandHistory.length - 1] !== query) {
        setCommandHistory((prev) => [...prev, query]);
      }
      setHistoryIndex(-1);
      setHistory((prev) => [...prev, { type: "command", text: query }]);

      if (query.toLowerCase() === "clear") {
        setHistory([]);
        return;
      }

      const useMatch = query.match(/^use\s+([a-zA-Z0-9_]+);?/i);
      if (useMatch) {
        setCurrentDb(useMatch[1]);
        setHistory((prev) => [
          ...prev,
          { type: "success", text: "Database changed" },
        ]);
        return;
      }

      setIsExecuting(true);
      try {
        const res = await api.post("/mysql-query", {
          serverId: selectedServer,
          query: query,
          currentDb: currentDb,
        });
        const asciiTable = formatAsAsciiTable(res.data.data);
        setHistory((prev) => [...prev, { type: "result", text: asciiTable }]);
      } catch (err) {
        const errorMsg = err.response?.data?.error || err.message;
        setHistory((prev) => [
          ...prev,
          { type: "error", text: `ERROR: ${errorMsg}` },
        ]);
      } finally {
        setIsExecuting(false);
        setTimeout(() => inputRef.current?.focus(), 50);
      }
    }
  };

  return (
    <Box maxWidth="xl" mx="auto">
      {/* 1. EKRAN: SUNUCU SEÇİMİ (VISION STYLE) */}
      {!isTerminalOpen && (
        <Fade in={!isTerminalOpen}>
          <Box sx={{ mt: 4 }}>
            <Box
              sx={{ mb: 5, display: "flex", alignItems: "center", gap: 2.5 }}
            >
              <Box
                sx={{
                  p: 2,
                  borderRadius: 3,
                  background:
                    "linear-gradient(135deg, rgba(29,111,242,0.15) 0%, rgba(0,229,255,0.1) 100%)",
                  border: "1px solid rgba(29,111,242,0.2)",
                }}
              >
                <Storage sx={{ fontSize: 36, color: "#2da7ff" }} />
              </Box>
              <Box>
                <Typography variant="h4" sx={{ fontSize: "2.1rem" }}>
                  SQL <GradientText>Yöneticisi</GradientText>
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Güvenli veri tüneli üzerinden sorgularınızı koşturun.
                </Typography>
              </Box>
            </Box>

            <Card
              sx={{
                bgcolor: "background.paper",
                borderRadius: 4,
                maxWidth: 550,
                border: "1px solid rgba(234,240,248,0.05)",
                boxShadow: "0 20px 50px rgba(0,0,0,0.3)",
              }}
            >
              <CardContent
                sx={{ p: 4, display: "flex", gap: 3, flexDirection: "column" }}
              >
                <FormControl fullWidth variant="filled">
                  <InputLabel sx={{ color: "text.secondary" }}>
                    Hedef Veri Kaynağı
                  </InputLabel>
                  <Select
                    value={selectedServer}
                    onChange={(e) => setSelectedServer(e.target.value)}
                    disableUnderline
                    sx={{
                      borderRadius: 2,
                      bgcolor: "rgba(234,240,248,0.03)",
                      border: "1px solid rgba(234,240,248,0.05)",
                      "& .MuiSelect-select": { py: 2 },
                    }}
                  >
                    <MenuItem value="">
                      <em>Sunucu Seçin</em>
                    </MenuItem>
                    {servers.map((s) => (
                      <MenuItem key={s.id} value={s.id}>
                        <Stack
                          direction="row"
                          spacing={1.5}
                          alignItems="center"
                        >
                          <DnsRounded
                            fontSize="small"
                            sx={{ color: "primary.light" }}
                          />
                          <Typography variant="body1">{s.name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            ({s.ip_address})
                          </Typography>
                        </Stack>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Button
                  variant="contained"
                  onClick={handleConnect}
                  disabled={!selectedServer}
                  startIcon={<TerminalIcon />}
                  sx={{ py: 2, fontWeight: "bold" }}
                >
                  WORKBENCH'İ BAŞLAT
                </Button>
              </CardContent>
            </Card>
          </Box>
        </Fade>
      )}

      {/* 2. EKRAN: MYSQL TERMINAL (PREMIUM STYLE) */}
      {isTerminalOpen && (
        <Fade in={isTerminalOpen}>
          <Box
            sx={{
              height: "82vh",
              display: "flex",
              flexDirection: "column",
              mt: 1,
            }}
          >
            {/* Terminal Üst Barı */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                bgcolor: "rgba(17, 17, 22, 0.9)",
                p: 2,
                borderRadius: "16px 16px 0 0",
                border: "1px solid rgba(234,240,248,0.08)",
                borderBottom: "none",
                backdropFilter: "blur(20px)",
              }}
            >
              <Stack direction="row" spacing={1.2}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    bgcolor: "#FF5F56",
                  }}
                />
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    bgcolor: "#FFBD2E",
                  }}
                />
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    bgcolor: "#27C93F",
                  }}
                />
              </Stack>

              <Stack direction="row" spacing={1} alignItems="center">
                <Storage sx={{ color: "primary.main", fontSize: 18 }} />
                <Typography
                  variant="subtitle2"
                  sx={{ fontFamily: "monospace", opacity: 0.8 }}
                >
                  {serverObj?.ip_address}{" "}
                  {currentDb && (
                    <span style={{ color: "#3ddc97" }}>~ {currentDb}</span>
                  )}
                </Typography>
              </Stack>

              <Stack direction="row" spacing={1}>
                <Tooltip title="Temizle">
                  <IconButton
                    onClick={handleClearTerminal}
                    size="small"
                    sx={{ color: "text.secondary" }}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Bağlantıyı Kapat">
                  <IconButton
                    onClick={() => setIsTerminalOpen(false)}
                    size="small"
                    sx={{ color: "error.light" }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Box>

            {/* Terminal Gövdesi */}
            <Box
              onClick={() => inputRef.current?.focus()}
              sx={{
                flexGrow: 1,
                bgcolor: "#030305",
                borderRadius: "0 0 16px 16px",
                p: 3,
                overflowY: "auto",
                border: "1px solid rgba(234,240,248,0.08)",
                borderTop: "none",
                fontFamily: "'Fira Code', monospace",
                boxShadow: "inset 0 0 50px rgba(0,0,0,0.8)",
                "&::-webkit-scrollbar": { width: "6px" },
                "&::-webkit-scrollbar-thumb": {
                  bgcolor: "rgba(234,240,248,0.1)",
                  borderRadius: "10px",
                },
              }}
            >
              {history.map((line, idx) => (
                <Box key={idx} sx={{ mb: 1.5 }}>
                  {line.type === "command" && (
                    <Typography
                      sx={{
                        color: "#F8FAFC",
                        fontFamily: "inherit",
                        display: "flex",
                        gap: 1,
                      }}
                    >
                      <span style={{ color: "#2da7ff", fontWeight: "bold" }}>
                        mysql&gt;
                      </span>{" "}
                      {line.text}
                    </Typography>
                  )}
                  {line.type === "info" && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: "text.secondary",
                        fontFamily: "inherit",
                        fontStyle: "italic",
                        opacity: 0.6,
                      }}
                    >
                      // {line.text}
                    </Typography>
                  )}
                  {line.type === "success" && (
                    <Typography
                      sx={{
                        color: "#3ddc97",
                        fontFamily: "inherit",
                        fontWeight: 600,
                      }}
                    >
                      {line.text}
                    </Typography>
                  )}
                  {line.type === "error" && (
                    <Typography
                      sx={{ color: "#ff6b81", fontFamily: "inherit" }}
                    >
                      [ERROR] {line.text}
                    </Typography>
                  )}
                  {line.type === "result" && (
                    <Box
                      component="pre"
                      sx={{
                        color: "#2da7ff",
                        m: 0,
                        mt: 1,
                        p: 2,
                        bgcolor: "rgba(45, 167, 255, 0.03)",
                        borderRadius: 2,
                        fontFamily: "inherit",
                        fontSize: "0.8rem",
                        whiteSpace: "pre",
                      }}
                    >
                      {line.text}
                    </Box>
                  )}
                </Box>
              ))}

              {/* Aktif Giriş Satırı */}
              <Box sx={{ display: "flex", mt: 2, alignItems: "center" }}>
                <Typography
                  sx={{
                    color: "#2da7ff",
                    fontWeight: "bold",
                    fontFamily: "inherit",
                    mr: 1.5,
                  }}
                >
                  mysql&gt;
                </Typography>
                {isExecuting ? (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CircularProgress size={14} thickness={6} />
                    <Typography variant="caption" color="text.secondary">
                      Sorgu koşturuluyor...
                    </Typography>
                  </Stack>
                ) : (
                  <input
                    ref={inputRef}
                    type="text"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyDown={handleKeyDown}
                    autoComplete="off"
                    spellCheck="false"
                    style={{
                      flexGrow: 1,
                      background: "transparent",
                      border: "none",
                      outline: "none",
                      color: "#F8FAFC",
                      fontFamily: "inherit",
                      fontSize: "inherit",
                      caretColor: "#2da7ff",
                    }}
                  />
                )}
              </Box>
              <div ref={bottomRef} />
            </Box>

            {/* Alt Bilgi Barı */}
            <Box
              sx={{
                p: 1,
                px: 2,
                display: "flex",
                justifyContent: "flex-end",
                gap: 2,
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ opacity: 0.5 }}
              >
                Sorgu çalıştırmak için ENTER / Geçmiş için OK TUŞLARI
              </Typography>
            </Box>
          </Box>
        </Fade>
      )}
    </Box>
  );
}
